import type { Currency } from '@witmetrics/utils';
import { formatMetricValue } from '@/utils/metrics';
import ComparisonBars from '@/components/ComparisonBars';
import DeltaPill from '@/components/DeltaPill';
import OverviewCard from '@/components/OverviewCard';
import Skeleton from '@/components/Skeleton';
import TruncatedText from '@/components/TruncatedText';
import type { OverviewMetricRow } from '../types';
import { SKELETON } from './constants';
import { classes } from './utils';

export type KeyMetricsCardProps = {
  isFetchingData?: boolean;
  currency?: Currency;
  subtitle: string;
  currentLabel: string;
  previousLabel: string;
  rows: OverviewMetricRow[];
};

export default function KeyMetricsCard({
  isFetchingData,
  currency,
  subtitle,
  currentLabel,
  previousLabel,
  rows,
}: KeyMetricsCardProps) {
  const renderValue = (row: OverviewMetricRow) => {
    if (isFetchingData) return <Skeleton {...SKELETON.value} />;
    return (
      <>
        <span className={classes.value}>
          {formatMetricValue(row, row.current, currency)}
        </span>
        {row.delta && <DeltaPill {...row.delta} />}
      </>
    );
  };

  const renderCell = (row: OverviewMetricRow) => (
    <div key={row.key} className={classes.cell}>
      <TruncatedText className={classes.label} tooltipPlacement="top">
        {row.label}
      </TruncatedText>
      <div className={classes.valueRow}>{renderValue(row)}</div>
      <ComparisonBars
        showLabels
        previousLabel={previousLabel}
        currentLabel={currentLabel}
        previousValue={
          isFetchingData ? '' : formatMetricValue(row, row.previous, currency)
        }
        currentValue={
          isFetchingData ? '' : formatMetricValue(row, row.current, currency)
        }
        previousWidth={row.previousWidth}
        currentWidth={row.currentWidth}
      />
    </div>
  );

  return (
    <OverviewCard title="Key Metrics" subtitle={subtitle}>
      <div className={classes.grid}>{rows.map(renderCell)}</div>
    </OverviewCard>
  );
}
